import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from './UserContext';
import { supabase } from '../lib/supabaseClient';

export const CartContext = createContext();

// Clé du panier visiteur dans localStorage.
const GUEST_CART_KEY = 'cbjm:cart';

/* --- Panier visiteur (non connecté) --------------------------------------
 * Le panier doit fonctionner sans compte : exiger une inscription avant même
 * de pouvoir ajouter un article fait perdre la majorité des visiteurs. On
 * persiste donc le panier dans localStorage, et on le fusionne avec le panier
 * Supabase au moment où la personne se connecte.
 * Toutes ces fonctions sont sûres côté serveur (localStorage n'y existe pas).
 */
function readGuestCart() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(GUEST_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writeGuestCart(items) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch (e) {
    // Quota dépassé ou stockage désactivé : le panier reste en mémoire.
  }
}

function clearGuestCart() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(GUEST_CART_KEY);
  } catch (e) {}
}

/**
 * Fusionne deux paniers en additionnant les quantités des articles identiques.
 * Utilisé à la connexion : ce que la personne a mis au panier en visiteur
 * s'ajoute à ce qu'elle avait déjà enregistré sur son compte.
 */
function mergeCarts(base, extra) {
  const result = [...base];
  for (const item of extra) {
    const existing = result.find((i) => i.id === item.id);
    if (existing) {
      existing.quantity = (existing.quantity || 1) + (item.quantity || 1);
    } else {
      result.push({ ...item });
    }
  }
  return result;
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const prevCartRef = useRef([]);

  const { user, isLoadingUser } = useContext(UserContext);

  // 🛒 Chargement initial : localStorage pour un visiteur, Supabase pour un
  // compte connecté (avec fusion du panier visiteur éventuel).
  useEffect(() => {
    if (isLoadingUser) return;
    let annule = false;

    const charger = async () => {
      if (!user) {
        const local = readGuestCart();
        if (annule) return;
        setCart(local);
        prevCartRef.current = local;
        setIsReady(true);
        return;
      }

      // maybeSingle() plutôt que single() : un compte sans panier enregistré
      // est un cas normal, pas une erreur à logger.
      const { data, error } = await supabase
        .from('carts')
        .select('items')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) console.warn('[fetchCart ❌]', error.message);
      if (annule) return;

      const distant = Array.isArray(data?.items) ? data.items : [];
      const local = readGuestCart();
      const fusionne = local.length ? mergeCarts(distant, local) : distant;

      setCart(fusionne);
      // On garde le panier distant comme référence : si une fusion a eu lieu,
      // l'effet de sauvegarde détectera la différence et enverra le résultat.
      prevCartRef.current = distant;
      if (local.length) clearGuestCart();
      setIsReady(true);
    };

    charger();
    return () => {
      annule = true;
    };
  }, [user, isLoadingUser]);

  // 💾 Sauvegarde à chaque changement réel du panier.
  useEffect(() => {
    if (!isReady) return;
    if (JSON.stringify(prevCartRef.current) === JSON.stringify(cart)) return;
    prevCartRef.current = cart;

    if (!user) {
      writeGuestCart(cart);
      return;
    }

    const sauvegarder = async () => {
      const { error } = await supabase.from('carts').upsert({
        user_id: user.id,
        items: cart,
        updated_at: new Date().toISOString(),
      });
      if (error) console.warn('[saveCart ❌]', error.message);
    };

    sauvegarder();
  }, [cart, user, isReady]);

  // 🧠 Fonctions panier — toujours actives, connecté ou non.
  const addToCart = (product) => {
    setCart((currentCart) => {
      const existingItem = currentCart.find((item) => item.id === product.id);
      return existingItem
        ? currentCart.map((item) =>
            item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
          )
        : [...currentCart, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id, newQuantity) => {
    setCart((currentCart) =>
      currentCart
        .map((item) => (item.id === id ? { ...item, quantity: newQuantity } : item))
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (id) => {
    setCart((currentCart) => currentCart.filter((item) => item.id !== id));
  };

  // Vide le panier après un paiement abouti. checkout.js déstructurait déjà
  // `clearCart` alors que le provider ne l'exposait pas : la valeur était donc
  // `undefined`, et le panier restait rempli après un achat.
  const clearCart = () => {
    setCart([]);
    clearGuestCart();
  };

  // ⚠️ Ce provider ne doit JAMAIS retourner null, ni exposer des fonctions
  // vides.
  //
  // Il est monté via wrapRootElement (gatsby-ssr.js et gatsby-browser.js) et
  // enveloppe donc tout l'arbre React, y compris le composant `Head` de Gatsby.
  // Un `return null` supprimait le rendu de toutes les pages : plus de <title>,
  // plus de <meta>, plus de contenu HTML — 48 pages non indexées par Google.
  //
  // Et exposer `addToCart: () => {}` tant que l'utilisateur n'était pas
  // connecté rendait le bouton « Ajouter au panier » silencieusement inopérant
  // pour tous les visiteurs.
  const value = {
    cart,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    isCartLoading: isLoadingUser || !isReady,
  };

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
};
